/**
 * ss-ui-client migration checklist — steps to retire the local copy once this is stable:
 *
 * 1. Plug ss-ui-client's LogService into the shared logger at startup
 *    (add to src/setup-axios.js alongside the existing LogService import):
 *      import { configureLogger } from '@shipstation/client-common/services/logger';
 *      import { LogService } from './services/log-service';
 *      configureLogger(LogService);
 *
 * 2. Update these three callers to import submitZendeskSsoForm from this package and pass
 *    their configured axios instance as the second argument:
 *      ss-ui-client/src/components/app-header/help-menu-dropdown/hooks/use-get-help-item.tsx
 *      ss-ui-client/src/components/app-header/help-menu-dropdown/hooks/use-support-cases-item.tsx
 *      ss-ui-client/src/global/support-chat/open-sierra-in-zendesk.ts
 *    Pass the app's configured axios instance — not bare `import axios from 'axios'`,
 *    which would be unconfigured in apps that don't mutate global axios defaults.
 *    ss-ui-client configures global defaults in setup-axios.js, so bare axios works there:
 *      import axios from 'axios';
 *      await submitZendeskSsoForm(url, axios);
 *    Other apps (e.g. sslite) should pass their configured httpClient instead:
 *      import { httpClient } from '@shipstation/client-common/api/client';
 *      await submitZendeskSsoForm(url, httpClient);
 *
 * 3. Delete ss-ui-client/src/services/zendesk-sso-service.ts
 *
 * 4. Optionally: replace ss-ui-client HelpCenterService usages with named imports from
 *      '@shipstation/client-common/services/helpcenter-service'
 *    and delete ss-ui-client/src/services/helpcenter-service.js
 */

import { logger } from './logger';

import type { HttpClient } from './http-client';

type ZendeskLoginInfo = {
  zenDeskSsoRoute: string;
  jwt: string;
  returnTo: string;
};

const SSO_WINDOW_NAME = 'zendesk-sso';

// Returns true if the SSO form was submitted, false if a popup blocker prevented the
// window from opening or the login request failed/returned an empty body. Callers that
// need to fire follow-up effects (analytics, notifications) should branch on the return
// value. The window must open synchronously, before any await, so a blocked popup is
// still inside the click's user-gesture window and reports back as `false` — opening it
// after the login request would look identical to success even when nothing rendered.
export const submitZendeskSsoForm = async (zenDeskLoginUrl: string, httpClient: HttpClient): Promise<boolean> => {
  const ssoWindow = window.open('', SSO_WINDOW_NAME);
  if (!ssoWindow) {
    return false;
  }

  const zenDeskLoginInfo = await httpClient
    .get<ZendeskLoginInfo>(zenDeskLoginUrl)
    .then((response) => response?.data)
    .catch((error: unknown) => {
      logger.logError(error, { context: 'Error retrieving login info for Zendesk' });
    });

  if (!zenDeskLoginInfo?.zenDeskSsoRoute) {
    ssoWindow.close();
    return false;
  }

  const form = document.createElement('form');
  form.action = `${zenDeskLoginInfo.zenDeskSsoRoute}/access/jwt`;
  form.method = 'post';
  // Target the window opened above by name, instead of '_blank', so the JWT posts into the
  // same pre-opened tab. rel="noopener" is intentionally omitted — some browsers treat it as
  // forcing a brand-new context even when a same-name target already exists, which would
  // defeat re-targeting the pre-opened window. Zendesk is a trusted destination, so it
  // receiving an opener reference is low-risk.
  form.target = SSO_WINDOW_NAME;
  form.style.display = 'none';

  const jwtInput = document.createElement('input');
  jwtInput.value = zenDeskLoginInfo.jwt;
  jwtInput.name = 'jwt';
  form.appendChild(jwtInput);

  const returnToInput = document.createElement('input');
  returnToInput.value = zenDeskLoginInfo.returnTo;
  returnToInput.name = 'return_to';
  form.appendChild(returnToInput);

  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
  return true;
};
